import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedItem } from '../../components/PerinHealthGridSlice';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import FilterManagementView from '../../components/FilterManagementView';
import managementStyles from '../ManagementPages.module.css';
import styles from './HubManagement.module.css';
import { LeftArrowIcon, OnlineIcon, OfflineIcon } from '../../components/icons/Icons';
import PatientHubInfoPopup from './components/PatientHubInfoPopup';
import useAxios from '../../services/AxiosConfig'; // Import useAxios
import { useLocation } from 'react-router-dom';
import { formatDateString } from '../../utils/dateUtils';

const PatientHubManagement = () => {
    const location = useLocation();
    const groupName = location.state?.groupName || 'Default Group';
    const groupId = location.state?.groupId;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const accountId = useSelector((state) => state.userInfo.id);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState({ items: [] }); // Ensure items is always an array
    const [patientHubData, setPatientHubData] = useState([]);

    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const iconMapping = {
        'Online': { icon: <OnlineIcon />, text: 'Online' },
        'Offline': { icon: <OfflineIcon />, text: 'Offline' },
      };

    const headers = [
        { key: 'id', label: "ID" },
        { key: 'assignedTo', label: "Assigned to"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'status', label: "Status"},
        { key: 'groupName', label: "Group"},
        { key: 'dateAssigned', label: "Date Assigned"},
    ];

    useEffect(() => {
        dispatch(setSelectedItem({}));
        getHubs(); // Fetch hubs data on component mount
    }, [userDateFormat]);
    
    const formatDate = (dateString) => {
        if (!dateString) {
            return "N/A"; // Return "N/A" if dateString is null or undefined
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return "N/A"; // Check if the date object is valid
        }
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    };
    

    function formatTime(dateTimeString) {
        if (!dateTimeString) {
            return "N/A"; // Return "N/A" if dateTimeString is null or undefined
        }
        const time = new Date(dateTimeString);
        if (isNaN(time.getTime())) {
            return "N/A"; // Check if the datetime object is valid
        }
        return time.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };    

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return "N/A";
        const dateTime = new Date(dateTimeString);
        if (isNaN(dateTime.getTime())) return "N/A";
        const date = formatDateString(dateTime, userDateFormat);
        const time = formatTime(dateTime);
        return `${date} ${time}`;
    };
    

    const getHubs = async () => {    
        httpService.get(`hub/group/${accountId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const groupData = responseData.data.data.find(hub => hub.groupId === groupId);
                    
                    if (groupData) {
                        const filteredHubs = groupData.assignedHubs || [];
    
                        const formattedHubs = filteredHubs.map(hub => ({
                            id: hub.accountHubId,
                            assignedTo: `${hub.firstName} ${hub.lastName}`,
                            serialNumber: hub.serialNumber,
                            status: hub.status || 'Offline',
                            groupName: groupData.groupName,
                            dateAssigned: hub.AccountProgramHub_Genesis ? formatDateString(hub.AccountProgramHub_Genesis, userDateFormat) : "N/A",
                            lastTransmissionDate: hub.AccountProgramHub_LastTransmission ? formatDateString(hub.AccountProgramHub_LastTransmission, userDateFormat) : "N/A",
                            lastTransmissionTime: formatTime(hub.AccountProgramHub_LastTransmission),
                            lastConnection: formatDateTime(hub.AccountProgramHub_LastConnection),
                            providerName: hub.AccountProgramHub_ProviderName,
                            eID: hub.AccountProgramHub_eID
                        }));
    
                        setPatientHubData(formattedHubs);
                        setTotalHubs(groupData.quantity);
                        setAssignedHubs(filteredHubs.length);
                    } else {
                        console.error('Group data not found');
                    }
                } else {
                    console.error('Invalid data format');
                }
            })
            .catch((err) => {
                console.error('Error fetching hubs:', err);
            });
    };    
    
    const [totalHubs, setTotalHubs] = useState(0);
    const [assignedHubs, setAssignedHubs] = useState(0);

    const onRowClickHandler = (rowItem) => {
        const defaultData = {
            assignedTo: '',
            serialNumber: '',
            allStatuses: [], // Ensure this is always an array
            lastTransmissionDate: '',
            lastTransmissionTime: '',
            lastConnection: '',
            providerName: '',
            eID: ''
        };
    
        const rowData = {
            ...defaultData,
            ...rowItem
        };
    
        setPopupData(rowData);
        setShowPopup(true);
    };
       
     
    const onDeleteHandler = () => {
        // Define your delete logic here
    };

    const onBackHandler = () => {
        navigate('/hub-management');
    };

    const closePopup = () => {
        setShowPopup(false);
    };

    return (
        <div className={managementStyles.MiddleContent}>
            <div style={{display:"flex", alignItems:"center", columnGap:"5px"}}>
                <span className="subtitle-1">{`${groupName} |`}</span>
                <span className={styles.HubsNumber}>{assignedHubs}</span>
                <span className={styles.HubsLabel}>/{totalHubs}</span>
                <span className={styles.HubsLabel} style={{color:"#30584A"}}>Hubs Assigned</span>
            </div>
            <FilterManagementView
                isOrganization={false}
                isGroup={false}
                showAddButton={false}
                showDeleteButton={false}
                onDeleteHandler={onDeleteHandler}
                modelHeader='Delete Hub?' 
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={patientHubData}
                    headers={headers}
                    showCheckbox={false}
                    iconMapping={iconMapping} 
                    onDeleteRow={onDeleteHandler}   
                    onRowValueClick={onRowClickHandler}
                    rowValueAction = {'assignedTo'}
                    gridHeight='100%'
                />
            </div>
            {showPopup && 
            <PatientHubInfoPopup data={popupData} onClose={closePopup} />
        }
        </div>
    )
}

export default PatientHubManagement;
